import React, { useState } from 'react';
import moment from 'moment';
import EditIcon from '../../../../../../../assets/svg/solo-chat/pencil.svg';
import { DeleteIcon } from '../../../icons';
import { Table } from 'antd';
import AddIcon from '../../../../../../../assets/svg/solo-chat/circle-plus.svg';
import { EmptyState } from './EmptyState';
import style from './schema-layout.module.scss';
import { Modal } from '../../../components';
import { useAppAndBranchId, useQuery, useMutate, usePermissions } from '../../../hooks';
import { Entity } from '../types';
import { api } from '../api';
import { AddEntity } from '../components';
import { useToast } from '../../../../../../../helpers/commonFunction';
import { errorMessage } from '../../../utils/error';

type Props = {
	openEntityValue: (entity: Entity, onSave: () => void) => void;
};

export function Entities(props: Props) {
	const { openEntityValue } = props;
	const { ErrorToast } = useToast();
	const [appId, branchId] = useAppAndBranchId();
	const {
		loading,
		data: entities,
		setData: setEntities,
		refetch,
	} = useQuery<Entity[]>(() => {
		return api.getSchema(appId, branchId, 'entity');
	}, branchId);
	const { mutate: addMutate, loading: addLoading } = useMutate(api.addSchema);
	const { mutate: editMutate, loading: editLoading } = useMutate(
		api.editSchema,
	);
	const { mutate: deleteMutate, loading: deleteLoading } = useMutate(
		api.deleteSchema,
	);

	const dataSource = Array.isArray(entities) ? entities.map(entityMapper) : [];

	const [entityName, setEntityName] = useState('');
	const [entityId, setEntityId] = useState('');
	const [error, setError] = useState('');
	const [canCreate, canEdit, canDelete] = usePermissions(['nlu.Create', 'nlu.Edit', 'nlu.Delete']) as boolean[]
	const [modalVisibility, setModalVisibility] = useState({
		addEntity: false,
		editEntity: false,
		deleteEntity: false,
	});

	const deletEntityName = entities?.find((el) => el.id === entityId)?.name;

	function updateVisibility(key: string, visibility: boolean) {
		setModalVisibility((prev) => ({
			...prev,
			[key]: visibility,
		}));
	}

	function validateEntity(entity: string) {
		if (!entity.length) {
			setError('Enter entity name');
			return false;
		}

		const isExists = entities?.findIndex((el) => el.name === entity) !== -1;
		if (isExists) {
			setError('Entity name already exists');
			return false;
		}

		return true;
	}

	const columns = [
		{
			title: 'Entities',
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: 'Created At',
			dataIndex: 'createdAt',
			key: 'createdAt',
		},
		{
			title: 'Values',
			dataIndex: 'entityValues',
			key: 'entityValues',
			render: (_: any, record: Entity) => {
				const { entityValues } = record;
				if (!entityValues) {
					return null;
				}
				return (
					<div className={style.entityValues}>
						{entityValues.slice(0, 3).map((value) => {
							return (
								<p className={style.entityValue} key={value.id}>
									{value.name}
								</p>
							);
						})}
						{entityValues.length > 3 && (
							<p className={style.entityCount}>+{entityValues.length - 3}</p>
						)}
					</div>
				);
			},
		},
		{
			// this column is used to show "edit"/"delete" icons
			title: '',
			dataIndex: 'ghost',
			key: 'ghost',
			render: (_: any, record: Entity) => {
				return (
					<div className="solo-chat-table-actions">
						{canEdit && (
							<button
								onClick={(e) => {
									e.stopPropagation();
									setEntityId(record.id);
									updateVisibility('editEntity', true);
									setEntityName(record.name);
								}}
							>
								<img width="13" height="13" src={EditIcon} alt="edit-entity" />
							</button>
						)}
						{canDelete && (
							<button
								onClick={(e) => {
									e.stopPropagation();
									setEntityId(record.id);
									updateVisibility('deleteEntity', true);
								}}
							>
								<DeleteIcon width="13" height="13" stroke="#292D32" />
							</button>
						)}
					</div>
				);
			},
		},
	];

	return (
		<div>
			<div className={style.list}>
				<Table
					dataSource={dataSource}
					columns={columns}
					pagination={false}
					rowKey="id"
					loading={loading}
					onRow={(record) => {
						return {
							onClick: () => {
								openEntityValue(record, () => {
									refetch();
								});
							},
						};
					}}
				/>
				<div className={style.actions}>
					{canCreate && (
						<button
							className={style.add}
							onClick={() => updateVisibility('addEntity', true)}
						>
							<img src={AddIcon} alt="add entity" />
							<span>Add Entity</span>
						</button>
					)}
				</div>
			</div>

			<AddEntity
				onEntityNameChange={(entityName) => {
					setError('');
					setEntityName(entityName);
				}}
				loading={addLoading}
				error={error}
				onOk={async () => {
					if (!validateEntity(entityName.trim())) {
						return;
					}
					try {
						const entity = await addMutate(appId, {
							name: entityName.trim(),
							branchId,
							type: 'entity',
						});
						setEntities((prev) => {
							const entities = Array.isArray(prev) ? [...prev] : [];
							entities.push(entityMapper(entity));
							return entities;
						});
						updateVisibility('addEntity', false);
					} catch (err) {
						ErrorToast(errorMessage("add", "entity", err.code));
					}
				}}
				onCancel={() => {
					setError('');
					updateVisibility('addEntity', false);
				}}
				isOpen={modalVisibility.addEntity}
			/>

			<AddEntity
				error={error}
				isEdit
				initialEntityName={entityName}
				onEntityNameChange={(entityName) => {
					setError('');
					setEntityName(entityName);
				}}
				loading={editLoading}
				onOk={async () => {
					if (!validateEntity(entityName.trim())) {
						return;
					}
					try {
						const data = await editMutate(appId, {
							name: entityName.trim(),
							id: entityId,
							branchId,
							type: 'entity',
						});
						setEntities((prev) => {
							const entities = Array.isArray(prev) ? [...prev] : [];
							return entities.map((entity) =>
								entity.id === data.id ? { ...entity, name: data.name } : entity,
							);
						});
						updateVisibility('editEntity', false);
					} catch (err) {
						ErrorToast(errorMessage("edit", "entity", err.code));
					}
				}}
				onCancel={() => {
					updateVisibility('editEntity', false);
					setError('');
				}}
				isOpen={modalVisibility.editEntity}
			/>

			<Modal
				loading={deleteLoading}
				styles={{
					'--ok-btn-bg': '#DE0000',
					'--ok-btn-filter': 'drop-shadow(0px 8px 13px rgba(211, 0, 73, 0.18))',
				}}
				okText="Delete"
				onOk={async () => {
					try {
						await deleteMutate(appId, branchId, 'entities', entityId);
						setEntities((prev) => {
							const entities = Array.isArray(prev) ? [...prev] : [];
							return entities.filter((entity) => entity.id !== entityId);
						});
						updateVisibility('deleteEntity', false);
					} catch (err) {
						ErrorToast(errorMessage("delete", "entity", err.code));
					}
				}}
				onCancel={() => updateVisibility('deleteEntity', false)}
				isOpen={modalVisibility.deleteEntity}
				title="Remove Chosen Entity"
			>
				<div
					style={{
						padding: '18px 14px',
						color: '#393939',
						fontSize: 15,
						fontWeight: 500,
					}}
				>
					<p>Do you really want to remove the chosen entity?</p>
					{deletEntityName && (
						<span className="modal-delete-entity">{deletEntityName}</span>
					)}
				</div>
			</Modal>

			{!Boolean(dataSource.length) && !loading && <EmptyState type="entity" />}
		</div>
	)
}

function entityMapper(entity: Entity): Entity {
	return {
		...entity,
		createdAt: moment(entity.createdAt).format('DD MMM YYYY, hh:mm A'),
	};
}
